import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { useFetchRestaurantQuery } from '@/api/hooks/useRestaurant';
import { useFetchSubscriptionsQuery } from '@/api/hooks/useSubscription';
import { SubscriptionResponse } from '@/api/lib/subscription';

import statImage from '../../../../assets/images/stats.jpg';
import { Image } from '@/components/atoms/Image';
import { Spinner } from '@/components/atoms/Spinner';
import { Typography } from '@/components/atoms/Typography';
import { HeaderBoostBlock } from '@/components/molecules/HeaderBoostBlock';
import { PricingComparatif } from '@/components/molecules/PricingComparatif';
import { ReviewClientResto } from '@/components/molecules/ReviewClientResto';
import { SubscriptionCard } from '@/components/organisms/SubscriptionCard';
import { TabPageLayout } from '@/components/templates/TabPageLayout';

const tiers = [
  { name: 'Par défaut', id: 'defaut', priceMonthly: '0€', mostPopular: false },
  { name: 'Abonnement Publicitaire', id: 'pub', priceMonthly: '57€', mostPopular: true }
];

const sections = [
  {
    name: 'Visibilité',
    features: [
      { name: 'Présence en recherche', tiers: { 'Par défaut': true, 'Abonnement Publicitaire': true } },
      { name: 'Top #1 des résultats', tiers: { 'Par défaut': false, 'Abonnement Publicitaire': true } },
      { name: 'Boost des ventes', tiers: { 'Par défaut': false, 'Abonnement Publicitaire': true } },
      { name: 'Pop-Up de redirection', tiers: { 'Par défaut': false, 'Abonnement Publicitaire': true } },
      { name: 'Mise en avant du restaurant', tiers: { 'Par défaut': false, 'Abonnement Publicitaire': true } },
      { name: 'Stats par e-mail', tiers: { 'Par défaut': false, 'Abonnement Publicitaire': true } }
    ]
  },
  {
    name: 'Avantages',
    features: [
      { name: 'Présence de ma pub sur concurrents', tiers: { 'Par défaut': false, 'Abonnement Publicitaire': true } },
      { name: 'Pub des concurrents sur ma fiche', tiers: { 'Par défaut': true, 'Abonnement Publicitaire': false } },
      { name: 'Codes promo', tiers: { 'Par défaut': '2', 'Abonnement Publicitaire': '10' } },
      { name: 'Recommandations restos similaires', tiers: { 'Par défaut': true, 'Abonnement Publicitaire': false } },
      { name: 'Photos dans galerie', tiers: { 'Par défaut': '5', 'Abonnement Publicitaire': '15' } },
      { name: 'Multi-pub par ville', tiers: { 'Par défaut': true, 'Abonnement Publicitaire': true } }
    ]
  }
];

const reviews = [
  {
    rating: 5,
    content: 'Grâce à cette solution, mon restaurant a gagné en visibilité face à la concurrence locale. Une vraie différence !',
    author: 'Rodizio Brazil',
    city: 'Cergy'
    // avatarSrc: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQarwSYXh_mIs8m9B-c4FkUNdIF7RBTWpLHFw&s'
  },
  {
    rating: 5,
    content: 'Nos franchises profitent d’une couverture publicitaire optimale. Un levier efficace pour booster notre notoriété.',
    author: 'Cantine Corner',
    city: 'Coignières, Eragny, Noisy-le-Sec, Clichy'
    // avatarSrc: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTyR1Kc7uyxGuTt_Jd-CKS5MEjUU-ejqHDjj_ebZwheCXUqMC0hvIjGZbEqnCGGJHaB44w&usqp=CAU'
  },
  {
    rating: 5,
    content: 'L’abonnement publicitaire a clairement boosté notre clientèle !',
    author: 'Tacos Avenue',
    city: 'Alès'
    // avatarSrc: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR5Jk_-8rYSP-Dvma7h1CwnlYDitRV5fq5a0g&s'
  },
  {
    rating: 5,
    content: 'Nous avons observé une nette augmentation de la fréquentation grâce à l’abonnement pub. C’est devenu indispensable !',
    author: 'Uncle Bumpy',
    city: 'Saint-Ouen'
    // avatarSrc: 'https://ugc.production.linktr.ee/dda35350-ec62-4e1c-aae3-ced35efce8d1_UNCLE-BUMPY-STORY.png?io=true&size=avatar-v3_0'
  },
  {
    rating: 5,
    content: 'Ce service a permis à mes franchisés de capter une nouvelle clientèle. Les résultats sont là, je recommande vivement !',
    author: 'Chô Chaï',
    city: 'Paris, Nîmes, Montpellier, Toulouse, Lyon'
    // avatarSrc: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRfjTWthHHc_A4hlmt6OcGrr3QqYnBTqh9rsA&s'
  }
];

export const SubscriptionPage = () => {
  const { id } = useParams();
  const restaurantId = id || '';

  const { data: restaurant, isLoading: restaurantLoading } = useFetchRestaurantQuery({ id: restaurantId, withMenu: false }, { enabled: !!restaurantId });
  const { data: subscriptionData, isLoading: subscriptionsLoading } = useFetchSubscriptionsQuery({ restaurantId });

  const subscriptions = subscriptionData?.subscriptions || [];
  const activeSubscriptions = subscriptions.filter((subscription) => subscription.active);

  const [showComparatif, setShowComparatif] = useState(activeSubscriptions ? false : true);

  return (
    <TabPageLayout bottomPadding={false} askBoostVisibility={false}>
      <section className="mt-12">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-xl font-semibold text-primary">Booster la visibilité de votre restaurant</h2>
          <p className="mt-2 text-5xl font-semibold tracking-tight text-gray-900 sm:text-6xl">Découvrez notre offre !</p>
        </div>
        <p className="mx-auto mt-6 max-w-4xl text-center font-medium text-gray-600 text-xl">
          <b>Augmentez votre visibilité</b> et <b>attirez plus de clients</b> en souscrivant à notre abonnement publicitaire. Profitez de nombreux avantages
          pour seulement 57€ par mois !
        </p>
      </section>

      <section className="border gap-5 rounded-xl">
        <HeaderBoostBlock restaurant={restaurant} />
      </section>

      {!activeSubscriptions && (
        <section className="border rounded-xl flex md:flex-row flex-col overflow-hidden">
          <div className="flex flex-col  p-10 items-center justify-center">
            <Typography variant="h1" weight="semibold" marginClassName="mb-5">
              Pourquoi souscrire à notre abonnement publicitaire ?
            </Typography>
            <Typography variant="paragraph">
              En souscrivant à notre abonnement publicitaire, vous bénéficiez de nombreux avantages pour booster la visibilité de votre restaurant et attirer
              plus de clients.
            </Typography>
          </div>
          <Image draggable={false} alt="charte" src={statImage} className="w-full md:w-fit max-w-3xl" />
        </section>
      )}

      {!activeSubscriptions && (
        <section>
          <ReviewClientResto reviews={reviews} />
        </section>
      )}

      <section>
        <button type="button" onClick={() => setShowComparatif(!showComparatif)} className="flex w-full justify-end hover:underline underline-offset-2">
          <Typography variant="paragraph">{showComparatif ? 'Fermer' : 'Afficher'} le comparatif des offres</Typography>
        </button>

        {showComparatif && <PricingComparatif sections={sections} tiers={tiers} />}
      </section>

      <section className="border gap-5 p-4 md:p-10 rounded-xl" id="subscriptions">
        <Typography variant="h1" weight="semibold" marginClassName="mb-1">
          Vos abonnements mensuels
        </Typography>
        <Typography variant="paragraph" marginClassName="mb-5">
          Pour accéder à vos factures, changer votre moyen de paiement ou annuler votre abonnement, cliquez sur le bouton permettant de gérer votre abonnement.
        </Typography>

        {subscriptionsLoading && (
          <div className="flex py-24 items-center justify-center">
            <Spinner big />
          </div>
        )}

        {activeSubscriptions ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-5">
            {activeSubscriptions.map((subscription: SubscriptionResponse) => (
              <SubscriptionCard
                key={subscription.id}
                restaurantName={subscription.restaurantName}
                restaurantCityId={subscription.cityId}
                city={subscription.cityName}
                active={subscription.active}
                customerId={subscription.customerId}
              />
            ))}
          </div>
        ) : (
          <Typography variant="h2" weight="semibold" align="center" marginClassName="mb-5">
            Vous n’avez pas encore d’abonnement
          </Typography>
        )}
      </section>
    </TabPageLayout>
  );
};
